/*
 * Knuska guarantee bar - the auto-scroll layout.
 *
 * Liquid renders the promises once, centred and still. That is the whole bar in
 * the row and grid layouts, and the fallback for the scrolling one. This file
 * only builds the loop, and only for the breakpoint that asked for it.
 *
 * The loop is exact rather than approximate: the authored set is repeated until
 * one lap is at least as wide as the bar, then the whole thing is cloned once,
 * so translating -50% lands the second half precisely where the first began.
 * A set narrower than the bar would otherwise drag a visible empty stretch
 * across it once per lap.
 *
 * Because the layout can differ per breakpoint, this has to be able to take the
 * loop apart again: crossing the breakpoint into a grid restores the authored
 * markup rather than leaving a track of duplicates arranged in columns.
 */
if (!customElements.get('knuska-guarantee')) {
  const DESKTOP = '(min-width: 750px)';
  const REDUCED = '(prefers-reduced-motion: reduce)';
  const MAX_REPEATS = 24;

  class KnuskaGuarantee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-kg-track]');
      this.source = this.querySelector('[data-kg-source]');
      if (!this.track || !this.source) return;

      this.baseHTML = this.source.innerHTML;
      this.speed = Number(this.dataset.speed) || 40;

      this.wide = window.matchMedia(DESKTOP);
      this.sync();

      /*
       * Fonts change how wide the labels are, so a track measured before they
       * land is built to the fallback's metrics and loops early.
       */
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.sync(true));
      }

      this.onChange = () => this.sync(true);
      if (this.wide.addEventListener) this.wide.addEventListener('change', this.onChange);
      else this.wide.addListener(this.onChange);

      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(() => this.schedule());
        this.observer.observe(this);
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onChange) {
        if (this.wide.removeEventListener) this.wide.removeEventListener('change', this.onChange);
        else this.wide.removeListener(this.onChange);
      }
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    schedule() {
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.clientWidth === this.builtFor) return;
        this.sync(true);
      });
    }

    /* Which layout is in force right now, and is it the moving one? */
    wants() {
      const layout = this.wide.matches ? this.dataset.desktop : this.dataset.mobile;
      if (layout !== 'scroll') return false;
      return !(window.matchMedia && window.matchMedia(REDUCED).matches);
    }

    sync(force) {
      if (this.wants()) this.build();
      else if (force || this.classList.contains('kg--scrolling')) this.teardown();
    }

    teardown() {
      this.classList.remove('kg--scrolling');
      this.style.removeProperty('--kg-duration');
      this.track.innerHTML = this.baseHTML;
      this.builtFor = null;
    }

    build() {
      const width = this.clientWidth;
      if (!width) return;

      this.classList.remove('kg--scrolling');
      this.track.innerHTML = this.baseHTML;

      let repeats = 1;
      while (this.track.scrollWidth < width && repeats < MAX_REPEATS) {
        this.track.insertAdjacentHTML('beforeend', this.baseHTML);
        repeats += 1;
      }

      const lap = this.track.scrollWidth;
      if (!lap) return;

      this.track.insertAdjacentHTML('beforeend', this.track.innerHTML);

      const items = Array.prototype.slice.call(this.track.children);
      items.slice(items.length / 2).forEach((item) => {
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('data-kg-clone', '');
        /* A duplicated promise must not be a second tab stop for one link. */
        const link = item.querySelector('a');
        if (link) link.setAttribute('tabindex', '-1');
      });

      this.style.setProperty('--kg-duration', lap / this.speed + 's');
      this.builtFor = width;
      this.classList.add('kg--scrolling');
    }
  }

  customElements.define('knuska-guarantee', KnuskaGuarantee);
}
